import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Plugin,
  ChartEvent,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useTheme } from "../context/ThemeContext";
import { candlestickPlugin, OHLCPoint } from "../utils/candlestickPlugin";

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Title, Tooltip, Legend);

interface Props {
  ohlcv: OHLCV[];
  symbol: string;
  /** Index (0-based) of the last "historical" bar — bars at and before this
   * index are the matched analog pattern; bars after it are the forward
   * outcome. Drawn as a dashed vertical divider, mirroring the boundary
   * marker the pure-SVG `CandlestickSparkline` strip drew. */
  historicalEndIndex?: number;
  className?: string;
}

const themeColorsFor = (isDark: boolean) => ({
  title: isDark ? "#E2E8F0" : "#0F172A",
  ticks: isDark ? "#94A3B8" : "#64748B",
  grid: isDark ? "rgba(71, 85, 105, 0.5)" : "rgba(203, 213, 225, 0.5)",
  // --signature: marks the historical/forecast boundary — the exact moment
  // the analog match "hands off" to the projected outcome, so it gets the
  // accent reserved for analog-matching rather than a neutral gray line.
  divider: isDark ? "rgba(251, 191, 36, 0.7)" : "rgba(245, 158, 11, 0.65)",
  tooltipBg: isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(15, 23, 42, 0.8)",
});

function formatBarDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

const fmtPrice = (v: number) => `$${v.toFixed(2)}`;

/**
 * Draws a dashed vertical line at the historical/forecast boundary — a small
 * custom plugin in the same spirit as `ForwardCurveFanChart`'s marker plugin,
 * rather than pulling in chartjs-plugin-annotation for one line.
 */
function buildDividerPlugin(index: number | null, color: string): Plugin<"bar"> {
  return {
    id: "historicalDivider",
    afterDatasetsDraw(chart) {
      if (index === null) return;
      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!chartArea || !xScale) return;
      const left = xScale.getPixelForValue(index);
      const right = xScale.getPixelForValue(index + 1);
      const x = (left + right) / 2;
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    },
  };
}

/**
 * Chart.js candlestick chart for Query Matcher analog match cards — the
 * detailed full-size view swapped in by `StockChartDisplay`'s Line/Candlestick
 * toggle (`matchChartMode`).
 *
 * Styled to match `PatternDetail`'s main candlestick chart (same
 * `candlestickPlugin` renderer/color convention, ticker title, `$`-formatted
 * price y-axis) per the user's request to look "similar to /patterns top
 * chart" — but with the x-axis (date) hidden, since match cards are compact
 * and the divider + hover tooltip already convey "where" within the analog a
 * given candle sits. Hover behavior mirrors `PatternDetail`'s RSI/sub-panel
 * charts: Chart.js's own index-mode tooltip (no custom floating-box
 * reimplementation), extended with an OHLC label callback.
 *
 * This supersedes the pure-SVG `CandlestickSparkline` for the *detailed* view
 * (that component still serves the small fixed-size strip elsewhere): the
 * detailed view is a 1-for-1 swap with `ModernStockChart` — never shown
 * alongside it — so using Chart.js here doesn't increase the per-card chart
 * instance count, and it gets title/axis/tooltip "for free" from Chart.js's
 * plugins instead of hand-built SVG/HTML overlays.
 */
export function MatchCandlestickChart({ ohlcv, symbol, historicalEndIndex, className }: Props) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const themeColors = themeColorsFor(isDark);

  const chartData = useMemo(() => {
    const candleData: OHLCPoint[] = ohlcv.map(([, o, h, l, c]) => ({ o, h, l, c }));
    return {
      labels: ohlcv.map(([d]) => d),
      datasets: [
        {
          label: `${symbol} Price`,
          data: ohlcv.map(() => 0),
          backgroundColor: "transparent",
          borderColor: "transparent",
          candleData,
        },
      ],
    };
  }, [ohlcv, symbol]);

  if (ohlcv.length === 0) return null;

  const dates = ohlcv.map(([d]) => d);
  const opens = ohlcv.map(([, o]) => o);
  const highs = ohlcv.map(([, , h]) => h);
  const lows = ohlcv.map(([, , , l]) => l);
  const closes = ohlcv.map(([, , , , c]) => c);

  const yMax = Math.max(...highs) * 1.01;
  const yMin = Math.min(...lows) * 0.99;

  const dividerIndex =
    historicalEndIndex !== undefined
    && historicalEndIndex >= 0
    && historicalEndIndex < ohlcv.length - 1
      ? historicalEndIndex
      : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (evt: ChartEvent) => {
      const target = evt.native?.target as HTMLElement | undefined;
      if (target) target.style.cursor = "crosshair";
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: symbol,
        align: "start" as const,
        font: { size: 14, weight: 600 as const, family: "Inter, sans-serif" },
        color: themeColors.title,
        padding: { top: 4, bottom: 12 },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: themeColors.tooltipBg,
        titleColor: "white",
        bodyColor: "white",
        bodyFont: { family: "Inter, sans-serif" },
        titleFont: { family: "Inter, sans-serif", weight: 600 as const },
        padding: 10,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          title: (items: { dataIndex: number }[]) => (items.length ? formatBarDate(dates[items[0].dataIndex]) : ""),
          label: (item: { dataIndex: number }) => {
            const i = item.dataIndex;
            return [
              `Open: ${fmtPrice(opens[i])}`,
              `High: ${fmtPrice(highs[i])}`,
              `Low: ${fmtPrice(lows[i])}`,
              `Close: ${fmtPrice(closes[i])}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        display: false,
        grid: { display: false },
      },
      y: {
        min: yMin,
        max: yMax,
        grid: { color: themeColors.grid },
        ticks: {
          color: themeColors.ticks,
          font: { family: "Inter, sans-serif", size: 10 },
          callback: (v: string | number) => fmtPrice(typeof v === "string" ? parseFloat(v) : v),
        },
      },
    },
  };

  return (
    <div className={["match-candlestick-chart", className].filter(Boolean).join(" ")} style={{ position: "relative" }}>
      <Chart
        type="bar"
        data={chartData as never}
        options={chartOptions as never}
        plugins={[candlestickPlugin, buildDividerPlugin(dividerIndex, themeColors.divider)]}
      />
    </div>
  );
}

export default MatchCandlestickChart;
